import moment from "moment"
import PropTypes from "prop-types"
import React from "react"
import { Column } from "react-virtualized"

import CSVButton from "@swipesense/csv-button"
import DataTable from "@swipesense/data-table"

import Loading from "../Loading"
import "./AssetHistoryTable.scss"

class AssetHistoryTable extends React.PureComponent {
  dateTimeFormatter = date => {
    const timeDisplay = moment.utc(date).format("hh:mm:ss a")
    const dateDisplay = moment.utc(date).format("MMM Do YYYY")
    return timeDisplay.slice(0, -1).concat(" ", dateDisplay)
  }

  dateTimeRenderer = ({ cellData }) => {
    if (cellData === null) {
      return "Exit not yet detected"
    }
    return this.dateTimeFormatter(cellData)
  }

  locationRenderer = ({ cellData }) => {
    return (
      <div className="location-data">
        <div className="location-name">{cellData.locationName}</div>
        <div className="unit-name">{cellData.unitName}</div>
      </div>
    )
  }

  sortByExitAt = (visitA, visitB) => {
    const visitADateTime = moment.utc(visitA.exitAt)
    const visitBDateTime = moment.utc(visitB.exitAt)
    // moment implicitly converts nulls to current datetime, so check for nulls first
    // before sorting by the moment-converted format
    if (visitA.exitAt === null) {
      return -1
    }
    if (visitB.exitAt === null) {
      return 1
    }
    if (visitADateTime > visitBDateTime) {
      return -1
    }
    if (visitADateTime < visitBDateTime) {
      return 1
    }
    return 0
  }

  csvHeaders = [
    { label: "Asset ID", key: "assetId" },
    { label: "Badge HWID", key: "badgeHwid" },
    { label: "Device Type", key: "deviceType" },
    { label: "Location", key: "location" },
    { label: "Unit", key: "unit" },
    { label: "Entry At", key: "entryAt" },
    { label: "Exit At", key: "exitAt" },
  ]

  csvDataMap = visit => ({
    assetId: visit.assetId,
    badgeHwid: visit.badgeHwid,
    deviceType: visit.deviceType,
    entryAt: this.dateTimeFormatter(moment.utc(visit.entryAt)),
    exitAt: this.dateTimeFormatter(moment.utc(visit.exitAt)),
    location: visit.location.locationName,
    unit: visit.location.unitName,
  })

  addDataFields = data =>
    data.map(visit => ({
      ...visit,
      assetId: this.props.data.assetId,
      badgeHwid: this.props.data.badgeHwid,
      deviceType: this.props.data.deviceType,
    }))

  render() {
    const sortedData = this.props.data.visits
      ? this.props.data.visits.sort(this.sortByExitAt)
      : []
    const csvFileName = `Swipesense Asset Visit Report ${new Date().toLocaleDateString()}.csv`
    const csvData = this.addDataFields(sortedData)
    return (
      <div className="AssetHistoryTable">
        <CSVButton
          data={csvData}
          filename={csvFileName}
          headers={this.csvHeaders}
          mapData={this.csvDataMap}
        />
        <DataTable
          data={sortedData}
          rowHeight={40}
          rowCount={sortedData.length}
          className="history-data-table"
          headerClassName="table-header"
          loading={this.props.loading}
          loadingComponent={<Loading />}
          noRowsMessage="No movement history has been recorded for this asset. Try a longer time frame."
        >
          <Column
            width={10}
            dataKey="location"
            label="LOCATION"
            disableSort={true}
            cellRenderer={this.locationRenderer}
          />
          <Column
            width={10}
            dataKey="entryAt"
            label="Entry At"
            disableSort={true}
            cellRenderer={this.dateTimeRenderer}
          />
          <Column
            width={10}
            dataKey="exitAt"
            label="Exit At"
            disableSort={true}
            cellRenderer={this.dateTimeRenderer}
          />
        </DataTable>
      </div>
    )
  }
}

AssetHistoryTable.propTypes = {
  id: PropTypes.string.isRequired,
  data: PropTypes.shape({
    deviceType: PropTypes.string,
    assetId: PropTypes.string,
    badgeHwid: PropTypes.string,
    visits: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        entryAt: PropTypes.string,
        exitAt: PropTypes.string,
        location: {
          locationName: PropTypes.string,
          unitName: PropTypes.string,
        },
      })
    ),
  }),
  loading: PropTypes.bool,
  startAt: PropTypes.object,
  endAt: PropTypes.object,
}

AssetHistoryTable.defaultProps = {
  data: {
    visits: [],
  },
  loading: false,
}

export default AssetHistoryTable
